import { Node } from '@xyflow/react';
import {
  buildNode,
  getEdgeId,
} from 'src/context/import-configuration/build-project-json-data/import-topology-shared';
import NodeData, {
  EDataType,
  NodeDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import { NodeModel } from 'src/modules/network-configuration/models/node-model';
import { generateNodeId } from 'src/modules/network-configuration/models/node-utils';
import { ProjectJsonData } from 'src/types/project';

export const DEVICE_NODE_TYPES = Object.values(NodeDataType).filter(
  (t) => NodeData[t]?.type === EDataType.Device,
);

function mergeCategoryOnTypeChange(
  previous: NodeCategoryModel,
  base: NodeCategoryModel,
): NodeCategoryModel {
  return {
    ...base,
    name: previous.name,
    ip: previous.ip,
    subnet: previous.subnet,
    subnetMask: previous.subnetMask,
    gateway: previous.gateway,
    dhcp: previous.dhcp,
    ipInterfaces: previous.ipInterfaces,
    poe: previous.poe,
    validationErrors: undefined,
  };
}

export function replaceDeviceNodeType(
  project: ProjectJsonData,
  nodeId: string,
  newType: NodeDataType,
): ProjectJsonData {
  const node = project.nodes.find((n) => n.id === nodeId) as
    | Node<NodeModel>
    | undefined;
  if (!node) return project;

  const currentType = node.type as string;
  const currentPrefix = `${currentType}-`;
  const nextCandidateId = nodeId.startsWith(currentPrefix)
    ? `${newType}-${nodeId.slice(currentPrefix.length)}`
    : `${newType}-${generateNodeId()}`;
  const nextNodeId =
    nextCandidateId === nodeId ||
    !project.nodes.some((n) => n.id === nextCandidateId && n.id !== nodeId)
      ? nextCandidateId
      : `${newType}-${generateNodeId()}`;

  const base = NodeData[newType];
  const merged = mergeCategoryOnTypeChange(node.data.category, base);
  const { position } = node;
  const rebuilt = buildNode({
    id: nextNodeId,
    nodeType: newType,
    category: merged,
    position,
  });

  const replaceNodeIdInHandle = (handle?: string | null) => {
    if (!handle) return handle;
    if (!handle.startsWith(`${nodeId}__`)) return handle;
    return `${nextNodeId}${handle.slice(nodeId.length)}`;
  };

  const edges = project.edges.map((edge) => {
    const source = edge.source === nodeId ? nextNodeId : edge.source;
    const target = edge.target === nodeId ? nextNodeId : edge.target;
    const sourceHandle = replaceNodeIdInHandle(edge.sourceHandle);
    const targetHandle = replaceNodeIdInHandle(edge.targetHandle);
    const id =
      sourceHandle && targetHandle
        ? getEdgeId(sourceHandle, targetHandle)
        : edge.id.split(nodeId).join(nextNodeId);

    return {
      ...edge,
      id,
      source,
      target,
      sourceHandle,
      targetHandle,
    };
  });

  return {
    ...project,
    nodes: project.nodes.map((n) => (n.id === nodeId ? rebuilt : n)),
    edges,
    lastChanged: new Date().toISOString(),
  };
}

export function addGenericDevice(project: ProjectJsonData): ProjectJsonData {
  const type = NodeDataType.GenericDevice;
  const id = `${type}-${generateNodeId()}`;
  const base = NodeData[type];
  const category: NodeCategoryModel = {
    ...base,
    name: `New device`,
  };
  const node = buildNode({
    id,
    nodeType: type,
    category,
    position: { x: 120 + project.nodes.length * 12, y: 120 },
  });

  return {
    ...project,
    nodes: [...project.nodes, node],
    lastChanged: new Date().toISOString(),
  };
}

export function removeNodeAndEdges(
  project: ProjectJsonData,
  nodeId: string,
): ProjectJsonData {
  return {
    ...project,
    nodes: project.nodes.filter((n) => n.id !== nodeId),
    edges: project.edges.filter(
      (e) => e.source !== nodeId && e.target !== nodeId,
    ),
    lastChanged: new Date().toISOString(),
  };
}
